import * as React from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Plus } from 'lucide-react'
import { advertisementService } from '@/services/advertisement_service'
import { CreateAdvertisement } from '@magnetar/admin-api-interfaces'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'

interface FormValues {
  title: string
  description: string
  is_active: boolean
}

export function CreateAdvertisementDialog({ onRefresh }: { onRefresh?: () => void }) {
  const { id: supplierId } = useViewMode()
  const { token } = useAuth()
  const [open, setOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const form = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      is_active: true,
    },
  })

  const onSubmit = async (values: FormValues) => {
    if (!supplierId || !token) return

    setIsSubmitting(true)

    const data: CreateAdvertisement = {
      supplier_id: supplierId,
      title: values.title,
      description: values.description,
      is_active: values.is_active,
    }

    try {
      const response = await advertisementService.createAdvertisement(data, token)

      if (response.success) {
        toast.success('廣告已建立')
        setOpen(false)
        form.reset()
        onRefresh?.()
      } else {
        toast.error(response.error?.message || '建立廣告失敗')
      }
    } catch {
      toast.error('建立廣告失敗')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          新增廣告
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>新增廣告</DialogTitle>
          <DialogDescription>請填寫廣告資訊，所有欄位都是必填的。</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              rules={{ required: '請輸入廣告標題' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>廣告標題</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入廣告標題" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              rules={{ required: '請輸入廣告描述' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>廣告描述</FormLabel>
                  <FormControl>
                    <Textarea placeholder="請輸入廣告描述" className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">啟用狀態</FormLabel>
                    <FormDescription>設定此廣告是否要立即啟用</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? '儲存中...' : '儲存'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
